import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import ChallengeReview from '../components/ChallengeReview';
import {
  Trophy,
  Timer,
  Medal,
  Crown,
  Check,
  X,
  ArrowRight,
  SpinnerGap,
  Calendar,
  Users,
  Star
} from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WeeklyChallengePage = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const [challenge, setChallenge] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    let interval;
    if (isPlaying && startTime && !submitted) {
      interval = setInterval(() => {
        setTimeElapsed(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, startTime, submitted]);

  const loadData = async () => {
    try {
      const [challengeRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/weekly-challenge/current`, { withCredentials: true }),
        axios.get(`${API}/weekly-challenge/leaderboard`, { withCredentials: true })
      ]);
      
      setChallenge(challengeRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      
      if (challengeRes.data.user_submission) {
        setSubmitted(true);
        setResult(challengeRes.data.user_submission);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startChallenge = () => {
    setIsPlaying(true);
    setStartTime(Date.now());
    setCurrentQuestion(0);
    setAnswers([]);
  };

  const selectAnswer = (answer) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = answer;
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < challenge.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const submitChallenge = async () => {
    if (answers.length < challenge.questions.length) {
      toast.error('Bitte beantworte alle Fragen');
      return;
    }

    setSubmitted(true);
    try {
      const res = await axios.post(`${API}/weekly-challenge/submit`, {
        answers: answers,
        time_taken: timeElapsed
      }, { withCredentials: true });
      
      setResult(res.data);
      toast.success(`${res.data.message} (+${res.data.points_earned} Punkte)`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Einreichen');
      setSubmitted(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Crown size={20} className="text-amber-500" weight="fill" />;
    if (rank === 2) return <Medal size={20} className="text-slate-400" weight="fill" />;
    if (rank === 3) return <Medal size={20} className="text-orange-400" weight="fill" />;
    return <span className="text-sm font-bold text-[#78716C]">{rank}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <SpinnerGap size={40} className="animate-spin text-[#AB1C24]" />
      </div>
    );
  }

  // Playing state
  if (isPlaying && !submitted) {
    const question = challenge.questions[currentQuestion];
    const progress = ((currentQuestion + 1) / challenge.questions.length) * 100;

    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="weekly-challenge-active">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Trophy size={24} className="text-amber-500" weight="fill" />
              <span className="font-heading font-bold text-lg">Wochen-Challenge</span>
            </div>
            <div className="flex items-center gap-2 bg-[#F5F5F4] px-4 py-2 rounded-xl">
              <Timer size={20} className="text-[#AB1C24]" />
              <span className="font-mono font-bold">{formatTime(timeElapsed)}</span>
            </div>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="text-sm text-[#78716C] mt-2">
            Frage {currentQuestion + 1} von {challenge.questions.length}
          </p>
        </div>

        {/* Question */}
        <div className="bg-white rounded-2xl border border-[#D6D3D1] p-8">
          {question.level && (
            <span className={`level-badge ${question.level} mb-4 inline-block`}>{question.level}</span>
          )}
          <h2 className="font-heading text-xl font-bold text-[#292524] mb-6">
            {question.question}
          </h2>

          <div className="grid gap-3">
            {question.options.map((option, idx) => (
              <button
                key={idx}
                onClick={() => selectAnswer(option)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  answers[currentQuestion] === option
                    ? 'border-[#AB1C24] bg-[#AB1C24]/5'
                    : 'border-[#D6D3D1] hover:border-[#AB1C24]/50'
                }`}
                data-testid={`option-${idx}`}
              >
                <span className="font-medium">{option}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between gap-4">
          <Button
            variant="outline"
            onClick={prevQuestion}
            disabled={currentQuestion === 0}
            className="flex-1"
          >
            Zurück
          </Button>
          
          {currentQuestion < challenge.questions.length - 1 ? (
            <Button
              onClick={nextQuestion}
              disabled={!answers[currentQuestion]}
              className="flex-1 bg-[#AB1C24] hover:bg-[#8A151C]"
            >
              Weiter
              <ArrowRight size={20} className="ml-2" />
            </Button>
          ) : (
            <Button
              onClick={submitChallenge}
              disabled={answers.length < challenge.questions.length}
              className="flex-1 bg-[#6B8E23] hover:bg-[#5a7a1e]"
              data-testid="submit-weekly-btn"
            >
              Abschicken
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Main view
  return (
    <div className="space-y-6" data-testid="weekly-challenge-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
          <Trophy size={28} weight="duotone" className="text-amber-500" />
          {lang === 'es' ? 'Desafío Semanal' : 'Wochen-Challenge'}
        </h1>
        <p className="text-[#57534E] mt-1">{lang === 'es' ? '¡Pon a prueba tus conocimientos y compite con la comunidad!' : 'Teste dein Wissen und messe dich mit der Community!'}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Challenge Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <div className={`rounded-2xl p-8 text-center ${
            submitted 
              ? 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200' 
              : 'bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200'
          }`}>
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-white shadow-lg flex items-center justify-center">
              {submitted ? (
                <Check size={40} className="text-green-600" weight="bold" />
              ) : (
                <Trophy size={40} className="text-amber-500" weight="fill" />
              )}
            </div>

            {submitted && result ? (
              <>
                <h2 className="font-heading text-2xl font-bold text-green-800 mb-2">
                  Teilnahme abgeschlossen!
                </h2>
                <p className="text-green-700 mb-4">
                  Du hast {result.score ?? challenge.user_submission?.score ?? 0} von {result.total ?? challenge.user_submission?.total ?? challenge?.questions?.length ?? 0} Fragen richtig beantwortet.
                </p>
                <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full">
                  <Star size={20} className="text-amber-500" weight="fill" />
                  <span className="font-bold text-[#292524]">
                    +{(result.score ?? challenge.user_submission?.score ?? 0) * 5} Punkte
                  </span>
                </div>
                {result.review && result.review.length > 0 && (
                  <div className="text-left">
                    <ChallengeReview review={result.review} lang={lang} />
                  </div>
                )}
              </>
            ) : (
              <>
                <h2 className="font-heading text-2xl font-bold text-amber-800 mb-2">
                  Diese Woche: {challenge?.week}
                </h2>
                <p className="text-amber-700 mb-6">
                  10 Fragen zu Vokabeln und Grammatik aus allen Niveaus. 
                  Schnelligkeit zählt bei Gleichstand!
                </p>
                <Button
                  onClick={startChallenge}
                  size="lg"
                  className="bg-[#AB1C24] hover:bg-[#8A151C] px-8"
                  data-testid="start-weekly-btn"
                >
                  <Trophy size={24} className="mr-2" />
                  Challenge starten
                </Button>
              </>
            )}
          </div>

          {/* How it works */}
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6">
            <h3 className="font-heading font-semibold text-lg text-[#292524] mb-4">
              So funktioniert's
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-xl font-bold text-blue-600">1</span>
                </div>
                <p className="text-sm text-[#57534E]">Beantworte 10 Fragen aus verschiedenen Niveaus</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-amber-100 flex items-center justify-center">
                  <span className="text-xl font-bold text-amber-600">2</span>
                </div>
                <p className="text-sm text-[#57534E]">Zeit wird gemessen - bei Gleichstand gewinnt der Schnellere</p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-xl font-bold text-green-600">3</span>
                </div>
                <p className="text-sm text-[#57534E]">Sammle Punkte und steige im Leaderboard auf</p>
              </div>
            </div>
          </div>
        </div>

        {/* Leaderboard */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-[#D6D3D1] p-6 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg text-[#292524]">
                Rangliste
              </h3>
              <div className="flex items-center gap-1 text-sm text-[#78716C]">
                <Users size={16} />
                <span>{leaderboard.length}</span>
              </div>
            </div>

            {leaderboard.length > 0 ? (
              <div className="space-y-2">
                {leaderboard.slice(0, 10).map((entry, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center gap-3 p-3 rounded-xl ${
                      entry.user_name === user?.name 
                        ? 'bg-[#AB1C24]/10 border border-[#AB1C24]/30' 
                        : idx < 3 
                          ? 'bg-gradient-to-r from-amber-50 to-transparent' 
                          : ''
                    }`}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      {getRankIcon(entry.rank)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-[#292524] truncate">
                        {entry.user_name}
                        {entry.user_name === user?.name && ' (Du)'}
                      </p>
                      <p className="text-xs text-[#78716C]">{entry.user_level}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#292524]">{entry.score}/{entry.total}</p>
                      <p className="text-xs text-[#78716C]">{formatTime(entry.time_taken)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Trophy size={40} className="mx-auto text-[#78716C]/30 mb-2" />
                <p className="text-sm text-[#78716C]">Noch keine Teilnehmer diese Woche</p>
                <p className="text-xs text-[#78716C]">Sei der Erste!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeeklyChallengePage;
